import React, { useMemo } from "react";
import { cn } from "../../../lib/utils";
import { useCurrentOrgUser } from "../../../auth/org-provider";
import Spinner from "../../../components/ui/spinner";
import { TekkrIcon } from "../tekkr/tekkr-logo";
import { AvatarForUser } from "../org/avatar-for-user";
import { FileMetadata } from "tekkr-common/dist/model/project/project";
import { UIMessage } from "ai";
import { Message } from "@ai-sdk/react";
import { AttachedFilesList } from "./attached-files-list";
import { GOOGLE_DRIVE_CONTENT_TYPE } from "../../../data/hooks/use-chat";

/**
 * @deprecated use ai sdk chat instead
 */
export type LegacyMessage = {
    role: "user" | "assistant";
    content: string;
    files: FileMetadata[];
};

export function MessageContainer({ role, children }: React.PropsWithChildren<{ role: Message["role"] }>) {
    const user = useCurrentOrgUser();
    const userName = useMemo(() => user.name.split(" ")[0], [user.name]);
    return (
        <div className={cn("flex flex-col gap-2", role === "user" ? "items-end" : "items-start")}>
            <div
                className={
                    "flex flex-row items-center gap-1 rounded-full bg-accent py-1.5 pe-3 ps-1.5 text-xs font-semibold"
                }
            >
                {role === "assistant" && <TekkrIcon className={"me-1 inline-block h-4 w-4"} />}
                {role === "user" && <AvatarForUser user={user} className={"me-1 inline-block h-4 w-4"} />}
                {role === "user" ? userName : "Assistant"}
            </div>
            <div className={cn(role === "user" ? "pe-2 ps-16" : "flex w-full flex-col items-start pe-16 ps-2")}>
                {children}
            </div>
        </div>
    );
}

export function MessageView({
    message,
    children,
}: {
    message: Pick<UIMessage, "role"> & Partial<Pick<Message, "experimental_attachments">>;
    children: React.ReactNode;
}) {
    const files = message.experimental_attachments?.map((f) => ({
        name: f.name ?? "Attached File",
        uploading: false,
        source: f.contentType === GOOGLE_DRIVE_CONTENT_TYPE ? ("google-drive" as const) : undefined,
    }));

    return (
        <MessageContainer role={message.role as "user" | "assistant"}>
            {children}
            {files && <AttachedFilesList files={files} />}
        </MessageContainer>
    );
}

export function AssistantLoadingIndicator() {
    return (
        <MessageContainer role={"assistant"}>
            <div
                className={
                    "flex flex-row items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-muted-foreground"
                }
            >
                <Spinner />
                Working on it...
            </div>
        </MessageContainer>
    );
}
